import {
  afterNextRender,
  Component,
  ElementRef,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, NgClass } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { SharedModule } from '../../shared/shared.module';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-dropdown-menu',
  template: `
    <div class="flex flex-col items-start xl:relative" (mouseenter)="isHoverDevice() && isOpen.set(true)" (mouseleave)="isHoverDevice() && isOpen.set(false)">
      <!-- Bouton qui ouvre le menu -->
      <app-link-highlight
        [forceActive]="(isDropdownPageActive$ | async) || isOpen()"
        (click)="toggleMenu()"
      >
        Louer ma salle
        <i
          class="far fa-chevron-down transition-transform duration-300"
          [ngClass]="{ 'rotate-180': isOpen() }"
        ></i>
      </app-link-highlight>

      <!-- Menu déroulant desktop (flottant) -->
      @if (isOpen()) {
        <div
          @slideDown
          class="hidden xl:block absolute top-full left-1/2 -translate-x-1/2 z-50 w-72 pt-4"
        >
          <div class="rounded-2xl bg-white shadow-xl ring-1 ring-black/5 overflow-hidden">
            <div class="py-2">
              @for (item of items; track item.route) {
                <a
                  [routerLink]="item.route"
                  (click)="toggleMenu()"
                  class="group flex flex-col px-4 py-3 no-underline hover:bg-gray-50 transition-colors"
                >
                  <span class="text-sm font-medium text-secondary-900">{{ item.label }}</span>
                  <span class="mt-0.5 text-xs text-gray-400">{{ item.description }}</span>
                </a>
              }
            </div>
          </div>
        </div>
      }

      <!-- Menu intégré mobile (dans le flux) -->
      @if (isOpen()) {
        <div @slideDown class="xl:hidden mt-3 flex flex-col items-start gap-1.5 pl-4">
          @for (item of items; track item.route) {
            <a
              [routerLink]="item.route"
              routerLinkActive="!underline underline-offset-4"
              [routerLinkActiveOptions]="{ exact: true }"
              (click)="toggleMenu()"
              class="text-sm font-medium text-secondary-900 no-underline rounded-full px-4 py-2 transition-colors hover:bg-gray-200"
            >{{ item.label }}</a>
          }
        </div>
      }
    </div>
  `,
  imports: [NgClass, SharedModule, AsyncPipe, RouterLink, RouterLinkActive],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class DropdownMenuComponent {
  private router = inject(Router);
  private dropdownPages = [
    '/louer-ma-salle/a-propos',
    '/louer-ma-salle/tarifs',
    '/auth/devenir-un-hote',
    '/louer-ma-salle/partenaires-revendeurs',
    '/louer-ma-salle/contact',
  ];
  isOpen = signal(this.dropdownPages.includes(this.router.url));
  isHoverDevice = signal(false);

  constructor(private elementRef: ElementRef) {
    afterNextRender(() => {
      this.isHoverDevice.set(window.matchMedia('(hover: hover) and (pointer: fine)').matches);
    });
  }

  items = [
    { label: 'Découvrir', description: 'La plateforme de réservation de salles', route: '/louer-ma-salle/a-propos' },
    { label: 'Tarifs', description: 'Abonnements adaptés à chaque besoin', route: '/louer-ma-salle/tarifs' },
    { label: 'Devenir hôte', description: 'Publiez et gérez vos salles facilement', route: '/auth/devenir-un-hote' },
    { label: 'Partenaires & revendeurs', description: 'Générez un revenu récurrent avec nous', route: '/louer-ma-salle/partenaires-revendeurs' },
    { label: 'Contact', description: 'Notre équipe répond rapidement', route: '/louer-ma-salle/contact' },
  ];

  isDropdownPageActive$: Observable<boolean> = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map(() => this.dropdownPages.includes(this.router.url)),
  );

  toggleMenu() {
    this.isOpen.set(!this.isOpen());
  }

  // Ferme le menu si on clique ailleurs
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }
}
